import {
  type OpenChangeReason,
  autoUpdate,
  flip,
  hide,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
  useTransitionStyles,
} from '@floating-ui/react';
import { interactionResponse } from '@zillow/yield-callback';
import { type SyntheticEvent, useCallback, useMemo, useState } from 'react';

import { useReducedMotion } from '../../../hooks/use-reduced-motion';
import { CALENDAR_CHANGE_REASON } from '../../calendar/constants/calendar-change-reason';
import type { DatePickerRootPropsInterface } from '../types/date-picker-root.types';
import type {
  DatePickerFloatingContextInterface,
  UseDatePickerReturnInterface,
} from '../types/use-date-picker.types';

export function useDatePicker(props: Partial<DatePickerRootPropsInterface>): {
  datePicker: UseDatePickerReturnInterface;
  floating: DatePickerFloatingContextInterface;
} {
  const {
    closeModalOnDateSelect = false,
    defaultOpen,
    defaultValue,
    offset: offsetValue = 8,
    onOpenChange: setControlledOpen,
    onValueChange: setControlledValue,
    open: controlledOpen,
    overflowPadding = 8,
    placement = 'bottom-start',
    shouldAwaitInteractionResponse = true,
    shouldCloseOnViewportLeave = false,
    useClickProps,
    useDismissProps,
    useRoleProps,
    value: controlledValue,
  } = props;

  const [descriptionId, setDescriptionId] = useState<string | undefined>();

  const [pendingValue, setPendingValue] = useState<string | Date | undefined>(controlledValue);

  const resetPendingValue = useCallback(() => {
    setPendingValue(controlledValue ?? '');
  }, [controlledValue]);

  const [uncontrolledValue, setUncontrolledValue] = useState<string | Date | undefined>(
    defaultValue,
  );

  const isControlledValue = 'value' in props;
  const value = isControlledValue ? controlledValue : uncontrolledValue || '';

  /**
   * Open state management (controlled or uncontrolled)
   */
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlledOpen = controlledOpen !== undefined;
  const open = isControlledOpen ? controlledOpen : uncontrolledOpen || false;

  /**
   * Floating UI setup for modal state management
   */
  const { context, refs, floatingStyles, middlewareData } = useFloating({
    placement,

    open,

    onOpenChange: async (state, event, reason) => {
      if (shouldAwaitInteractionResponse) {
        await interactionResponse();
      }

      if (reason === 'escape-key') {
        resetPendingValue?.();
        onValueChange?.(value ?? '', { reason: CALENDAR_CHANGE_REASON.escKey });
      }

      updateOpenState(state, event, reason);
    },

    whileElementsMounted: autoUpdate,

    /**
     * Middleware plugins have to be in certain order.
     * See: https://floating-ui.com/docs/useFloating#middleware
     */
    middleware: [
      // Translates the floating element along the specified axes
      offset(offsetValue),

      // Changes the placement of the floating element to keep it in view
      flip({
        fallbackPlacements: ['top-start', 'top-end', 'bottom-start', 'bottom-end'],
        padding: overflowPadding,
      }),

      // A data provider that allows you to hide the floating element
      ...(shouldCloseOnViewportLeave ? [hide({ padding: overflowPadding })] : []),
    ],
  });

  /**
   * Floating UI interaction hooks for accessibility and behavior
   * Sets up click and dismiss event handlers, and ARIA attributes based on role
   */
  const click = useClick(context, {
    enabled: true,
    event: 'mousedown',
    ...useClickProps,
  });

  const dismiss = useDismiss(context, {
    enabled: true,
    outsidePress: (event) =>
      !(event.target as HTMLElement).closest('[data-c11n-component="DatePicker.Root"]'),
    ...useDismissProps,
  });

  const role = useRole(context, {
    role: 'dialog',
    ...useRoleProps,
  });

  const { getFloatingProps, getReferenceProps } = useInteractions([click, dismiss, role]);

  /**
   * Motion helpers and styles
   */
  const reducedMotion = useReducedMotion();

  const { isMounted, styles: floatingTransitionStyles } = useTransitionStyles(context, {
    duration: reducedMotion ? 0 : 250,
    initial: {
      opacity: 0,
      transform: 'scale(0.975)',
    },
  });

  /**
   * Handles controlled or uncontrolled "open" state.
   *
   * @param newValue - New state
   * @param event - Event object
   * @param reason - Reason for state change
   */
  const updateOpenState = useCallback(
    (newValue: boolean, event?: Event, reason?: OpenChangeReason) => {
      if (isControlledOpen) {
        setControlledOpen?.(newValue, event, reason);
      } else {
        setUncontrolledOpen(newValue);
        setControlledOpen?.(newValue, event, reason);
      }
    },
    [isControlledOpen, setControlledOpen],
  );

  /**
   * Handles `DatePicker.Close` click actions.
   *
   * @param event - Event object
   */
  const onClickClose = useCallback(
    (event: SyntheticEvent) => {
      updateOpenState(false, event as unknown as Event, 'click');
    },
    [updateOpenState],
  );

  /**
   * Handles programmatic close action.
   */
  const onClose = useCallback(() => {
    updateOpenState(false);
  }, [updateOpenState]);

  const onValueChange = useCallback(
    (date: Date | string, meta: { reason: string }) => {
      setPendingValue(date);

      // for manual input, update the actual value immediately
      if (meta.reason === CALENDAR_CHANGE_REASON.manualInput) {
        if (isControlledValue) {
          setControlledValue?.(date, meta);
        } else {
          setUncontrolledValue(date);
          setControlledValue?.(date, meta);
        }
        return;
      }

      // For calendar selections, follow the closeModalOnDateSelect behavior
      if (
        meta.reason !== CALENDAR_CHANGE_REASON.ok &&
        meta.reason !== CALENDAR_CHANGE_REASON.cancel &&
        !closeModalOnDateSelect
      ) {
        return;
      }

      if (isControlledValue) {
        setControlledValue?.(date, meta);
      } else {
        setUncontrolledValue(date);
        setControlledValue?.(date, meta);
      }

      if (
        closeModalOnDateSelect ||
        meta.reason === CALENDAR_CHANGE_REASON.ok ||
        meta.reason === CALENDAR_CHANGE_REASON.cancel
      ) {
        onClose();
      }
    },
    [isControlledValue, closeModalOnDateSelect, setControlledValue, onClose],
  );

  const datePicker = useMemo(
    () => ({
      // Open/mount state and close handlers
      closeModalOnDateSelect,
      open,
      isMounted,
      onClickClose,
      onClose,

      // Value props and handlers
      pendingValue,
      setPendingValue,
      resetPendingValue,
      onValueChange,
      value,

      // Accessibility props and handlers
      descriptionId,
      setDescriptionId,

      // Refs
      refs,

      // Trigger and reference props
      getReferenceProps,

      // Styles
      floatingStyles,
      floatingTransitionStyles,

      middlewareData,
    }),
    [
      closeModalOnDateSelect,
      open,
      isMounted,
      onClickClose,
      onClose,
      pendingValue,
      setPendingValue,
      resetPendingValue,
      onValueChange,
      value,
      descriptionId,
      refs,
      getReferenceProps,
      floatingStyles,
      floatingTransitionStyles,
      middlewareData,
    ],
  );

  /*
   * Floating-ui-bridged data. Keep it on a separate context so only
   * DatePicker.Content pays for fresh Floating UI values.
   */
  const floating = { context, getFloatingProps };

  return { datePicker, floating };
}
